/* global debounce */

if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.slideshow = this.querySelector('.slideshow');
      this.allSlides = Array.from(this.querySelectorAll('.slideshow__slide'));
      this.slides = this.allSlides;
      this.nav = this.querySelector('.slideshow-nav');
      this.pagination = this.querySelector('.slideshow-pagination');
      this.rtl = document.dir === 'rtl';
      this.currentIndex = 0;
      this.swipeThreshold = 80;
      this.wheelTriggerWait = 800;

      if (this.nav) {
        this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
        this.counterTotal = this.querySelector('.slideshow-nav__counter-total');
        this.nav.addEventListener('click', this.handleNavClick.bind(this));
      }

      if (this.pagination) {
        this.pageBtns = Array.from(this.querySelectorAll('.page-btn'));
        // Remember which slide each button belongs to, as data-index is remapped
        // to the visible slides whenever slides are shown/hidden by CSS.
        this.pageBtns.forEach((btn) => {
          if (btn.dataset.slide === undefined) btn.dataset.slide = btn.dataset.index;
        });
        this.pagination.addEventListener('click', this.handlePaginationClick.bind(this));
      }

      if (theme.device.hasTouch) {
        this.slideshow.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
        this.slideshow.addEventListener('touchmove', debounce(this.handleTouchMove.bind(this), 200), { passive: true });
      }

      this.slideshow.addEventListener('wheel', this.handleWheel.bind(this), { passive: true });

      this.slideshow.addEventListener('transitionend', SlideShow.handleTransitionend);

      this.refreshSlides(true);
      this.watchSlideVisibility();
    }

    /**
     * Whether a slide is hidden by CSS (its own display, or an ancestor's).
     * @param {Element} el - Slide element.
     * @returns {boolean}
     */
    static isSlideHidden(el) {
      return el.getClientRects().length === 0;
    }

    /**
     * Recalculates which slides are actually visible and syncs the slideshow to
     * them. Slides hidden with CSS - for example by an A/B testing tool that
     * only ships a stylesheet - are excluded from the arrows, pagination,
     * counter, autoplay and swipe handling, so a slideshow left with a single
     * visible slide behaves like a static banner.
     * @param {boolean} force - Rebuild even when the visible slides are unchanged.
     */
    refreshSlides(force = false) {
      const visible = this.allSlides.filter((el) => !SlideShow.isSlideHidden(el));

      // If everything measures as hidden the section itself is off-screen or
      // display:none, so keep the current set rather than emptying it.
      const slides = visible.length ? visible : this.allSlides;

      if (!force && slides.length === this.slides.length
        && slides.every((el, index) => el === this.slides[index])) return;

      this.slides = slides;
      this.singleSlide = this.slides.length < 2;
      this.classList.toggle('slideshow--single', this.singleSlide);

      // Make sure the active slide is one that is actually visible.
      const active = this.allSlides.find((el) => el.classList.contains('is-active'));
      let index = this.slides.indexOf(active);

      if (index === -1) {
        if (active) active.classList.remove('is-active', 'transition-out');
        index = 0;
        this.slides[0].classList.add('is-active');
        this.slideshow.scrollTo({ left: this.slides[0].offsetLeft, behavior: 'auto' });
      }

      this.currentIndex = index;

      if (this.counterCurrent) this.counterCurrent.textContent = this.currentIndex + 1;
      if (this.counterTotal) this.counterTotal.textContent = this.slides.length;

      this.setSlideVisibility();
      this.updatePagination();
      this.refreshAutoplay();
    }

    /**
     * Watches for slides being shown or hidden by CSS after init - a late
     * arriving A/B test stylesheet, or a media query changing on resize.
     * A hidden element resizes to 0x0, so a ResizeObserver catches both.
     */
    watchSlideVisibility() {
      if ('ResizeObserver' in window === false) return;

      this.slideObserver = new ResizeObserver(debounce(() => this.refreshSlides(), 100));
      this.allSlides.forEach((el) => this.slideObserver.observe(el));
    }

    /**
     * Starts or stops autoplay to match the number of visible slides.
     */
    refreshAutoplay() {
      const autoplay = this.dataset.autoplay === 'true' && !this.singleSlide;
      if (autoplay === Boolean(this.autoplayEnabled)) return;

      if (autoplay) {
        this.initAutoPlay();
      } else {
        this.autoplayEnabled = false;
        this.stopAutoplay();
      }
    }

    initAutoPlay() {
      this.autoplayEnabled = true;
      this.autoplaySpeed = this.dataset.speed * 1000;

      if (!this.autoplayBound) {
        this.autoplayBound = true;

        this.addEventListener('mouseover', this.pauseAutoplay.bind(this));
        this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
        this.slideshow.addEventListener('focusin', this.pauseAutoplay.bind(this));
        this.slideshow.addEventListener('focusout', this.resumeAutoplay.bind(this));

        this.addObserver();
      }

      this.startAutoplay();
    }

    /**
     * Handles 'click' events on the nav buttons container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (evt.target.matches('.autoplay-btn')) {
        this.togglePlayState(evt);
        return;
      }

      if (this.singleSlide) return;

      if (evt.target.name === 'next') {
        this.showNextSlide();
      } else if (evt.target.name === 'prev') {
        this.showPrevSlide();
      }
    }

    /**
     * Handles 'click' events on the pagination.
     * @param {object} evt - Event object.
     */
    handlePaginationClick(evt) {
      if (!evt.target.matches('.page-btn') || this.singleSlide) return;

      const index = Number(evt.target.dataset.index);
      if (Number.isNaN(index) || index < 0) return;

      this.autoplayTimeLeft = this.autoplaySpeed;
      this.setActiveSlide(index);
    }

    /**
     * Handles 'wheel' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleWheel(evt) {
      if (this.singleSlide) return;

      if (this.blockWheelTrigger) {
        evt.preventDefault();
      } else if (evt.deltaX > 20) {
        evt.preventDefault();
        this.showNextSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      } else if (evt.deltaX < -20) {
        evt.preventDefault();
        this.showPrevSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      }
    }

    /**
     * Handles 'touchstart' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchStart(evt) {
      this.touchStart = evt.changedTouches[0].screenX;
    }

    /**
     * Handles 'touchmove' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchMove(evt) {
      if (this.singleSlide) return;

      const touchEnd = evt.changedTouches[0].screenX;

      if (touchEnd < this.touchStart - this.swipeThreshold) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      }

      if (touchEnd > this.touchStart + this.swipeThreshold) {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    /**
     * Handles 'transitionend' events on the slideshow.
     * @param {object} evt - Event object.
     */
    static handleTransitionend(evt) {
      if (!evt.target.matches('.has-motion')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      if (this.singleSlide) return;

      let index = this.currentIndex + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      if (this.singleSlide) return;

      let index = this.currentIndex - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    /**
     * Sets the active slide.
     * @param {number} slideIndex - Index of the slide to show.
     * @param {string} slideDirection - Direction for slide transitions.
     */
    setActiveSlide(slideIndex, slideDirection) {
      if (slideIndex === this.currentIndex) return;

      // Cancel any prior calls to this that have not completed
      clearTimeout(this.setActiveSlideTimeoutId);

      // Clear autoplay timer
      clearTimeout(this.autoplayTimer);

      // Set data attribute for direction of slide transitions.
      let dir = slideDirection || (slideIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) dir = dir === 'next' ? 'prev' : 'next';
      this.dataset.direction = dir;

      // Trigger text overlay transition out.
      this.slides[this.currentIndex].classList.add('transition-out');

      this.setActiveSlideTimeoutId = setTimeout(() => {
        // Move new slide into the viewport.
        this.slideshow.scrollTo({ left: this.slides[slideIndex].offsetLeft, behavior: 'auto' });

        // Update current slide and trigger transitions.
        this.slides[slideIndex].classList.add('is-active');
        this.slides[this.currentIndex].classList.remove('is-active', 'transition-out');

        // Add class to transitioned content element (if it exists).
        const transitionedEl = this.slides[this.currentIndex].querySelector('.has-motion');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        // Reset autoplay slide timer.
        if (this.autoplayEnabled) this.autoplayStartTime = Date.now();

        // Update slide counter.
        if (this.counterCurrent) this.counterCurrent.textContent = slideIndex + 1;

        this.currentIndex = slideIndex;
        this.setSlideVisibility();
        this.updatePagination();

        // Autoplay
        if (this.autoplayEnabled && !this.autoplayPaused) {
          this.autoplayTimeLeft = this.autoplaySpeed;
          this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
        }
      }, 200);
    }

    /**
     * Updates the 'aria-current' attribute of the pagination buttons.
     */
    updatePagination() {
      if (!this.pagination) return;

      this.pageBtns.forEach((btn) => {
        const slide = this.allSlides[Number(btn.dataset.slide)];
        const index = this.slides.indexOf(slide);

        // Hide the button of a slide that CSS has hidden, and remap the rest to
        // their position within the visible slides.
        btn.hidden = index === -1;
        btn.dataset.index = index;

        if (index !== -1 && index === this.currentIndex) {
          btn.setAttribute('aria-current', 'true');
        } else {
          btn.removeAttribute('aria-current');
        }
      });
    }

    stopAutoplay() {
      this.setSlideshowState('paused');
      clearTimeout(this.autoplayTimer);
    }

    startAutoplay() {
      this.autoplayTimeLeft = null;
      this.autoplayStartTime = Date.now();
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
      this.setSlideshowState('running');
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.stopAutoplay();

      if (this.autoplayTimeLeft) {
        this.autoplayTimeLeft -= (Date.now() - this.resumedTime);
      } else {
        this.autoplayTimeLeft = this.autoplaySpeed - (Date.now() - this.autoplayStartTime);
      }
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || !this.autoplayPaused) return;

      this.resumedTime = Date.now();
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplayTimeLeft);

      this.setSlideshowState('running');
    }

    /**
     * Adds an observer to pause autoplay when the slideshow is not in the viewport.
     */
    addObserver() {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      });

      observer.observe(this);
    }

    /**
     * Sets 'aria-hidden' and 'tabindex' of the slides and text overlay buttons.
     */
    setSlideVisibility() {
      const currentSlide = this.slides[this.currentIndex];

      this.allSlides.forEach((el) => {
        const btns = el.querySelectorAll('a');

        if (el === currentSlide) {
          el.setAttribute('aria-hidden', 'false');
          el.removeAttribute('tabindex');

          btns.forEach((btn) => {
            btn.removeAttribute('tabindex');
          });
        } else {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');

          btns.forEach((btn) => {
            btn.setAttribute('tabindex', '-1');
          });
        }
      });
    }

    /**
     * Sets 'aria-live' state of the slideshow and 'play-state' of the pagination animation.
     * @param {string} state - State to set ('running' or 'paused').
     */
    setSlideshowState(state) {
      this.style.setProperty('--play-state', state);
      this.slideshow.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
      this.autoplayPaused = state === 'paused';
    }

    /**
     * Toggles the autoplay state.
     * @param {object} evt - Event object.
     */
    togglePlayState(evt) {
      evt.target.classList.toggle('is-paused');
      this.autoplayEnabled = !this.autoplayEnabled;
      this.dataset.autoplay = this.autoplayEnabled ? 'true' : 'false';
      this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
    }
  }

  customElements.define('slide-show', SlideShow);
}
